// The day close's figures from the ledger (spec/13 §10, F-OPS-06), beside the canonical metrics they sit with.
//
// The day close is a **drawer** view, not a revenue view: its tenders are what each till, card machine and QR took,
// wallet top-ups included (the cash is in the drawer, whatever it bought), so the manager's count reconciles. What
// the business *earned* that day is M-01 (`revenueOf`), which the review shows beside them from the same cells. Both
// live here so the day close never adds tenders up on its own (spec/17 §1.1).
import { DISCOUNTS_ACCOUNT_KIND, PAYMENT_ACCOUNT_KINDS, REVENUE_EXCLUDED_ENTRY_KINDS, revenueOf, SALES_ACCOUNT_KINDS, TAX_PAID_AT_TOPUP_PREFIX, TAX_PAYABLE_PREFIX, WALLET_CREDIT_ENTRY_KINDS, } from "./ledger-metrics.js";
/** The day close's tenders (spec/13 §10). */
export const DAY_CLOSE_TENDERS = ['cash', 'counter_card', 'counter_upi', 'wallet'];
/** The day close's categories (spec/13 §10). */
export const DAY_CLOSE_CATEGORIES = ['play', 'food', 'retail', 'other'];
/** The tender accounts a venue takes money into, by the day-close tender (spec/06 §2.1). */
const TENDER_ACCOUNTS = {
    cash: 'tender_cash',
    counter_card: 'tender_counter_card',
    counter_upi: 'tender_counter_upi',
    wallet: 'tender_wallet',
};
/** Entry kinds that return money to a player from a sale (their tender lines are the day's refunds, spec/06 §2.3). */
const SALE_REFUND_KIND = 'refund';
/** Entries that give a sale's or a pass's money back: a sale refund and a pass refund (spec/06 §2.3, §8.7). */
const REFUND_ENTRY_KINDS = new Set([SALE_REFUND_KIND, 'pass_refund']);
/** Entries that sell something: a sale's settlement and a pass sold on its own (spec/06 §2.3). */
const SALE_ENTRY_KINDS = new Set(['sale_settlement', 'pass_sale']);
/**
 * The day's figures from the venue's ledger activity (spec/13 §10):
 *
 *   tenders          cash, counter card and counter UPI: the account's net movement (they are money in the till or
 *                    the terminal). The wallet's is a clearing account the spend credits and the settlement debits,
 *                    so its takings are the settlement's debits less the refunds' credits.
 *   categories       each `sales_*` account's net credit, refunds excluded (they are reported on their own), and
 *                    the wallet credit the venue gives away excluded (a bonus is not a sale; reported on its own).
 *   refunds          the tender credits of the sale refunds (`refund` entries).
 *   discounts        the `discounts` contra account's net debit, net of the releases a refund makes.
 *   receivables      the debits to players' `receivable` accounts on entries shared with the venue.
 *   pass sales       the `sales_play` credits of pass sales and sale settlements (a counter time line sells a pass).
 *   wallet top-ups   the venue tenders' debits on `wallet_topup` entries.
 *   wallet credit    the `sales_other` net debit of the wallet-credit entries (`WALLET_CREDIT_ENTRY_KINDS`).
 */
export function dayCloseFiguresOf(cells) {
    const venueCells = cells.filter((cell) => cell.ownerType === 'venue');
    const sum = (pick, where, from = venueCells) => from.filter(where).reduce((total, cell) => total + pick(cell), 0n);
    const tenderKinds = new Set(Object.values(TENDER_ACCOUNTS));
    const tenders = {};
    for (const tender of DAY_CLOSE_TENDERS) {
        const kind = TENDER_ACCOUNTS[tender];
        tenders[tender] =
            tender === 'wallet'
                ? sum((cell) => cell.debit, (cell) => cell.accountKind === kind && SALE_ENTRY_KINDS.has(cell.entryKind)) -
                    sum((cell) => cell.credit, (cell) => cell.accountKind === kind && cell.entryKind === SALE_REFUND_KIND)
                : sum((cell) => cell.debit - cell.credit, (cell) => cell.accountKind === kind);
    }
    const categories = {};
    for (const category of DAY_CLOSE_CATEGORIES) {
        const kind = SALES_ACCOUNT_KINDS[category];
        categories[category] = sum((cell) => cell.credit - cell.debit, (cell) => cell.accountKind === kind && isSaleEntry(cell.entryKind));
    }
    return {
        tenders,
        categories,
        refundMinor: sum((cell) => cell.credit, (cell) => tenderKinds.has(cell.accountKind) && cell.entryKind === SALE_REFUND_KIND),
        discountsMinor: sum((cell) => cell.debit - cell.credit, (cell) => cell.accountKind === 'discounts'),
        receivablesRaisedMinor: sum((cell) => cell.debit, (cell) => cell.accountKind === 'receivable', cells),
        passSalesMinor: sum((cell) => cell.credit, (cell) => cell.accountKind === 'sales_play' && SALE_ENTRY_KINDS.has(cell.entryKind)),
        walletTopUpsMinor: sum((cell) => cell.debit, (cell) => tenderKinds.has(cell.accountKind) && cell.entryKind === 'wallet_topup'),
        walletCreditGivenMinor: sum((cell) => cell.debit - cell.credit, (cell) => cell.accountKind === SALES_ACCOUNT_KINDS.other && WALLET_CREDIT_ENTRY_KINDS.has(cell.entryKind)),
    };
}
/** An entry whose `sales_*` lines are a sale on the day close: not a refund, not wallet credit, not a revenue-excluded move. */
function isSaleEntry(entryKind) {
    return !REFUND_ENTRY_KINDS.has(entryKind) && !WALLET_CREDIT_ENTRY_KINDS.has(entryKind) && !REVENUE_EXCLUDED_ENTRY_KINDS.has(entryKind);
}
const PAYMENT_ACCOUNTS = new Set([...Object.values(PAYMENT_ACCOUNT_KINDS), 'wallet_cash_tax_paid']);
const DAY_SALES_ACCOUNTS = new Set(DAY_CLOSE_CATEGORIES.map((category) => SALES_ACCOUNT_KINDS[category]));
/** The day's reconciliation from sales to revenue (`DayRevenueBridge`); `null` when the day has no revenue (M-01 null). */
export function dayRevenueBridgeOf(cells) {
    const revenue = revenueOf(cells);
    if (revenue === null)
        return null;
    let sales = 0n;
    let discounts = 0n;
    let tax = 0n;
    let taxPaidAtTopUp = 0n;
    let refunds = 0n;
    let onAccount = 0n;
    let walletBonus = 0n;
    let walletSettling = 0n;
    let other = 0n;
    for (const cell of cells) {
        if (REVENUE_EXCLUDED_ENTRY_KINDS.has(cell.entryKind) || WALLET_CREDIT_ENTRY_KINDS.has(cell.entryKind))
            continue;
        if (PAYMENT_ACCOUNTS.has(cell.accountKind))
            continue;
        const net = cell.debit - cell.credit;
        const isSales = DAY_SALES_ACCOUNTS.has(cell.accountKind) && cell.ownerType === 'venue';
        const isTax = cell.accountKind.startsWith(TAX_PAYABLE_PREFIX);
        const isTaxPaid = cell.accountKind.startsWith(TAX_PAID_AT_TOPUP_PREFIX);
        const isDiscount = cell.accountKind === DISCOUNTS_ACCOUNT_KIND;
        if (REFUND_ENTRY_KINDS.has(cell.entryKind) && (isSales || isTax || isTaxPaid || isDiscount))
            refunds += net;
        else if (isSales)
            sales -= net;
        else if (isTax)
            tax -= net;
        else if (isTaxPaid)
            taxPaidAtTopUp -= net;
        else if (isDiscount)
            discounts += net;
        else if (cell.accountKind === 'receivable')
            onAccount += net;
        else if (cell.accountKind === 'wallet_bonus')
            walletBonus += net;
        else if (cell.accountKind === 'tender_wallet')
            walletSettling += net;
        else
            other += net;
    }
    return { sales, discounts, tax, taxPaidAtTopUp, refunds, onAccount, walletBonus, walletSettling, other, revenue: revenue.total };
}
//# sourceMappingURL=day-close.js.map